
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNotify } from '@/hooks/useNotify';
import { Trash2, Plus, Edit, Save, X, LockIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Trade {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
}

const TradesSettings = () => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTrade, setNewTrade] = useState({ name: '', description: '' });
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null);
  

  useEffect(() => {
    if (profile?.company_id) {
      fetchTrades();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_all_trades', { user_company_id: profile?.company_id })
        .order('is_default', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      setTrades(data || []);
    } catch (error) {
      console.error('Error fetching trades:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addTrade = async () => {
    try {
      if (!newTrade.name.trim()) {
        notify.warning("Required Field", "Trade name is required.");
        return;
      }

      if (!profile?.company_id) {
        notify.warning("Missing Company", "You must be associated with a company to add a trade.");
        return;
      }

      const { data, error } = await supabase
        .from('supplier_trades')
        .insert({
          name: newTrade.name.trim(),
          description: newTrade.description || null,
          company_id: profile.company_id,
          is_default: false
        })
        .select();

      if (error) throw error;

      if (data) {
        setTrades([...trades, data[0]]);
        setNewTrade({ name: '', description: '' });
        notify.success("Trade added successfully");
      }
    } catch (error) {
      console.error('Error adding trade:', error);
      notify.error(error);
    }
  };

  const startEdit = (trade: Trade) => {
    if (trade.is_default) {
      notify.warning("Cannot edit default trade", "Default trades cannot be modified.");
      return;
    }
    setEditingTrade({ ...trade });
  };

  const cancelEdit = () => {
    setEditingTrade(null);
  };

  const saveEdit = async () => {
    try {
      if (!editingTrade || !editingTrade.name.trim()) return;

      const { data, error } = await supabase
        .from('supplier_trades')
        .update({
          name: editingTrade.name.trim(),
          description: editingTrade.description
        })
        .eq('id', editingTrade.id)
        .select();

      if (error) throw error;

      if (data) {
        setTrades(trades.map(t => t.id === editingTrade.id ? data[0] : t));
        setEditingTrade(null);
        notify.success("Trade updated successfully");
      }
    } catch (error) {
      console.error('Error updating trade:', error);
      notify.error(error);
    }
  };

  const deleteTrade = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      notify.warning("Cannot delete default trade", "Default trades cannot be removed.");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('supplier_trades')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTrades(trades.filter(t => t.id !== id));
      notify.success("Trade deleted successfully");
    } catch (error) {
      console.error('Error deleting trade:', error);
      notify.error(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Item Trades</CardTitle>
        <CardDescription>Manage trades for your line items</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Add new trade form */}
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-12 sm:gap-4 mb-6">
            <div className="sm:col-span-5">
              <Input 
                placeholder="Trade name" 
                value={newTrade.name}
                onChange={(e) => setNewTrade({...newTrade, name: e.target.value})}
              />
            </div>
            <div className="sm:col-span-5">
              <Input 
                placeholder="Description (optional)" 
                value={newTrade.description}
                onChange={(e) => setNewTrade({...newTrade, description: e.target.value})}
              />
            </div>
            <div className="sm:col-span-2">
              <Button 
                onClick={addTrade} 
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Trades list */}
          <div className="space-y-2">
            {loading ? (
              <p className="text-center py-4 text-muted-foreground">Loading trades...</p>
            ) : trades.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">No trades found. Add your first trade above.</p>
            ) : (
              <>
                {trades.map((trade) => (
                  <div key={trade.id} className="flex flex-col gap-3 p-3 border rounded-md sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                    {editingTrade && editingTrade.id === trade.id ? (
                      <>
                        <div className="sm:col-span-5">
                          <Input 
                            value={editingTrade.name}
                            onChange={(e) => setEditingTrade({...editingTrade, name: e.target.value})}
                          />
                        </div>
                        <div className="sm:col-span-5">
                          <Input 
                            value={editingTrade.description || ''}
                            onChange={(e) => setEditingTrade({...editingTrade, description: e.target.value})}
                            placeholder="Description (optional)"
                          />
                        </div>
                        <div className="sm:col-span-2 flex gap-2 justify-end">
                          <Button size="icon" variant="outline" onClick={cancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                          <Button size="icon" onClick={saveEdit}>
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="sm:col-span-5 font-medium flex items-center gap-2">
                          {trade.name}
                          {trade.is_default && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <LockIcon className="h-3 w-3" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="sm:col-span-5 text-muted-foreground">
                          {trade.description || <em>No description</em>}
                        </div>
                        <div className="sm:col-span-2 flex gap-2 justify-end">
                          <Button 
                            size="icon" 
                            variant="outline" 
                            onClick={() => startEdit(trade)}
                            disabled={trade.is_default}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="outline" 
                            onClick={() => deleteTrade(trade.id, trade.is_default)}
                            disabled={trade.is_default}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TradesSettings;
